import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppProfile } from 'src/app/shared/app-profile/app-profile';
import { AuthService } from 'src/app/core/services/auth.service';

@Component({
  selector: 'app-patient-care-plan',
  templateUrl: './patient-care-plan.component.html',
  styleUrls: ['./patient-care-plan.component.scss']
})
export class PatientCarePlanComponent implements OnInit {
  @Input() patient: any;
  @Output() close = new EventEmitter<void>();

  activeTab: string = 'Summary';
  tabs = [
    'Summary',
    'Therapy data',
    'Prescription',
    'Equipment',
    'Rule set',
    'Documents',
    'Activity log',
    'Rules Outcomes'
  ];

  assignedRuleSet: any = null;
  allRuleSets: any[] = [];
  loadingRuleSets = false;

  // Demo data for Patient tasks tab
  patientTasks = [
    { name: 'Take medication', status: 'Open', dueDate: '2024-06-10' },
    { name: 'Schedule follow-up', status: 'Completed', dueDate: '2024-06-05' },
    { name: 'Blood test', status: 'Open', dueDate: '2024-06-12' }
  ];

  riskSearch = { ruleType: '', startDate: '', endDate: '' };
  riskResults: any[] = [];
  riskError: string = '';
  accessTokenMST: string = '';
  currentPage: number = 1;
  totalPages: number = 1;

  constructor(private http: HttpClient,
    private appProfile: AppProfile,
    private authService: AuthService
  ) {}

  ngOnInit() {
    if (this.activeTab === 'Rule set') {
      this.loadAssignedRuleSet();
      this.loadAllRuleSets();
    } else if (this.activeTab === 'Rules Outcomes') {
      this.fetchMSTToken();
    }
  }

  setTab(tab: string) {
    this.activeTab = tab;
    if (tab === 'Rule set') {
      this.loadAssignedRuleSet();
      this.loadAllRuleSets();
    } else if (tab === 'Rules Outcomes') {
      this.fetchMSTToken();
    }
  }

  fetchMSTToken() {
    const url = 'https://iam-client-test.us-east.philips-healthsuite.com/authorize/oauth2/token';
    const body = new URLSearchParams();
    body.append('grant_type', 'password');
    body.append('username', 'testampmkst');
    body.append('password', 'Philips@123');
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': 'Basic YW1wbWF1dGhkZXY6UGhpbGlwc0AxMjM='
    };
    this.http.post<any>(url, body.toString(), { headers }).subscribe({
      next: (res) => {
        if (res && res.access_token) {
          this.accessTokenMST = res.access_token;
        }
      },
      error: (err) => {
        console.error('Failed to fetch MST token', err);
      }
    });
  }

  // Quick date selection logic
  setQuickDate(option: 'today' | 'yesterday' | 'lastMonth') {
    const today = new Date();
    if (option === 'today') {
      this.riskSearch.startDate = this.riskSearch.endDate = today.toISOString().slice(0, 10);
    } else if (option === 'yesterday') {
      const yest = new Date(today);
      yest.setDate(today.getDate() - 1);
      this.riskSearch.startDate = this.riskSearch.endDate = yest.toISOString().slice(0, 10);
    } else if (option === 'lastMonth') {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      this.riskSearch.startDate = first.toISOString().slice(0, 10);
      this.riskSearch.endDate = last.toISOString().slice(0, 10);
    }
  }

  onRiskSearch(page: number = 1) {
    this.currentPage = page;
    const accessToken = this.accessTokenMST;
    if (!accessToken) {
      console.error('Access token is missing. Please ensure you are logged in.');
      return;
    }
    this.riskResults = [];
    this.riskError = '';
    const url = `https://ampm-qa-us.f93f-use1.bb5ef68.hsp.philips.com/mst/_search/${page}`;
    // Map Rule Type to KAFKA-TOPIC
    const ENVIRONMENT = 'QA';
    let kafkaTopic = '';
    switch (this.riskSearch.ruleType) {
      case 'Adherance Profile Rule':
        kafkaTopic = `STREAM.${ENVIRONMENT}.AMPM.RES.FCT.NOTIFY.RISKASSESSMENT-SCORE-CHANGE.0`;
        break;
      case 'Compliance Rule':
        kafkaTopic = `STREAM.${ENVIRONMENT}.AMPM.RES.FCT.NOTIFY.COMPLIANCE-EVENT.0`;
        break;
      case 'Reminder':
        kafkaTopic = `STREAM.${ENVIRONMENT}.AMPM.RES.FCT.NOTIFY.REMINDER-EVENT.0`;
        break;
      case 'Rulesets Rule':
        kafkaTopic = `STREAM.${ENVIRONMENT}.AMPM.RES.FCT.NOTIFY.RULESET-RULE-EVENT.0`;
        break;
      default:
        kafkaTopic = '';
    }
    const headers: any = {
      'SERVICE-PROVIDER': 'MSK',
      'KAFKA-TOPIC': kafkaTopic,
      'x-ampm-message-solution': 'src',
      'x-ampm-message-organizationId': '94767b89-6ca5-4972-98f4-a392f80e893a',
      'Authorization': `Bearer ${accessToken}`
    };
    // Convert start/end date to ISO format if present
    if (this.riskSearch.startDate) {
      const startISO = new Date(this.riskSearch.startDate).toISOString();
      headers['PUBLISHED-START-DATE'] = startISO;
    }
    if (this.riskSearch.endDate) {
      const endISO = new Date(this.riskSearch.endDate).toISOString();
      headers['PUBLISHED-END-DATE'] = endISO;
    }
    // headers['SEARCH-TYPE'] = 'VALUE';
    // headers['SEARCH-VALUE'] = this.patient?.name || '';
    this.http.get<any>(url, { headers }).subscribe({
      next: (res) => {
        if (res && res.matchingMsgs) {
          this.riskResults = res.matchingMsgs
            .map((msg: any) => {
              let value: any = {};
              try {
                value = JSON.parse(msg.msgValue);
              } catch {
                value = {};
              }
              const methodDisplay = value && value.method && value.method.coding && Array.isArray(value.method.coding) ? value.method.coding[0]?.display : '';
              const prediction = value && value.prediction && Array.isArray(value.prediction) ? value.prediction[0] : {};
              const probabilityDecimal = prediction && typeof prediction.probabilityDecimal !== 'undefined' ? prediction.probabilityDecimal : '';
              const whenPeriod = prediction && prediction.whenPeriod ? `${prediction.whenPeriod.start || ''} - ${prediction.whenPeriod.end || ''}` : '';
              const occurrenceDateTime = value && typeof value.occurrenceDateTime !== 'undefined' ? value.occurrenceDateTime : '';
              // Published Date: use msg.msgHeaders.x-ampm-message-publishedAt if available
              let publishedDate = '';
              try {
                const headersObj = JSON.parse(msg.msgHeaders);
                publishedDate = headersObj['x-ampm-message-publishedAt'] ? headersObj['x-ampm-message-publishedAt'].slice(0, 10) : '';
              } catch {}
              // Filter by date range if set
              if (this.riskSearch.startDate && this.riskSearch.endDate) {
                if (publishedDate < this.riskSearch.startDate || publishedDate > this.riskSearch.endDate) {
                  return null;
                }
              }
              return {
                display: methodDisplay,
                probabilityDecimal,
                whenPeriod,
                occurrenceDateTime,
                publishedDate,
              };
            })
            .filter((r: any) => r !== null);
          // Set totalPages from API response if available, else default to 1
          this.totalPages = res.totalPages || Math.ceil((res.totalMatchingMsgCount || this.riskResults.length) / 10) || 1;
        } else {
          this.riskError = 'No results found.';
          this.totalPages = 1;
        }
      },
      error: (err) => {
        this.riskError = 'API error: ' + (err?.message || 'Unknown error');
        this.totalPages = 1;
      }
    });
  }

  loadAssignedRuleSet() {
    debugger
    if (!this.patient?.id) return;
    this.loadingRuleSets = true;
    const headers = this.authService.getCommonHeaders('2.0.0');
    const orgId = this.authService.getCommonHeaders(this.authService.accessToken).get('org');
    const body = {
      subject: {
        identifier: {
          system: 'http://philips.com/ampm',
          value: 'dad01e3b-3958-4148-b9e5-fda9d37f7d13'
        },
        type: 'Patient'
      }
    };
    this.http.post<any>(`${this.appProfile.ampmUs}/pes/fhir/${orgId}/CarePlan/_search`, body, { headers })
      .subscribe({
        next: (res) => {
          if (res && res.entry && res.entry.length > 0) {
            // Assume first CarePlan is the assigned one
            this.assignedRuleSet = res.entry[0];
          } else {
            this.assignedRuleSet = null;
          }
          this.loadingRuleSets = false;
        },
        error: () => {
          this.assignedRuleSet = null;
          this.loadingRuleSets = false;
        }
      });
  }

  loadAllRuleSets() {
    // Optionally, load all PlanDefinitions for other rulesets
    // ...implement as needed, similar to rulesets.component.ts
  }

  editPatientName() {
    alert('Edit patient name feature coming soon!');
  }
}
